import { useCallback, useEffect, useRef, useState } from "react";

import MobileDrawer from "./MobileDrawer";
import { NAV_ITEMS } from "../data/content";
import { ScrollTrigger, gsap, prefersReducedMotion } from "../lib/motion";
import { scrollToSection } from "../lib/useSmoothScroll";
import "./Header.css";

export default function Header({ lenisRef, ready }) {
  const [stuck, setStuck] = useState(false);
  const [active, setActive] = useState("#main");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const headerRef = useRef(null);

  /* Sticky state — the reference swaps to a white bar with a soft shadow. */
  useEffect(() => {
    const trigger = ScrollTrigger.create({
      start: "top -110",
      end: 99999,
      onToggle: (self) => setStuck(self.isActive),
    });
    return () => trigger.kill();
  }, []);

  /* Track which section owns the viewport so the nav can say where you are. */
  useEffect(() => {
    const sections = NAV_ITEMS.map((item) => document.querySelector(item.href)).filter(Boolean);
    if (!sections.length) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry) => setActive(`#${entry.target.id}`));
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );

    sections.forEach((section) => io.observe(section));
    return () => io.disconnect();
  }, []);

  /* Drop the bar in once the loader has cleared. */
  useEffect(() => {
    if (!ready || prefersReducedMotion()) return undefined;
    const tween = gsap.fromTo(
      headerRef.current,
      { y: -24, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.7, ease: "power3.out", delay: 0.1 },
    );
    return () => tween.revert();
  }, [ready]);

  const go = useCallback(
    (event, href) => {
      event.preventDefault();
      setDrawerOpen(false);
      scrollToSection(lenisRef?.current, href, href === "#main" ? -200 : -90);
    },
    [lenisRef],
  );

  return (
    <>
      <header
        ref={headerRef}
        className={`ilab-header${stuck ? " is-stuck" : ""}`}
        data-drawer={drawerOpen ? "open" : "closed"}
      >
        <div className="ilab-header__inner">
          <a className="ilab-header__logo" href="#main" onClick={(e) => go(e, "#main")}>
            <img
              src="/assets/logo/logo.png"
              alt="Intelligence Lab — home"
              width="300"
              height="79"
            />
          </a>

          <nav className="ilab-header__nav" aria-label="Main">
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={(e) => go(e, item.href)}
                    className={active === item.href ? "is-active" : undefined}
                    aria-current={active === item.href ? "true" : undefined}
                  >
                    <span>{item.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <button
            type="button"
            className="ilab-header__burger"
            onClick={() => setDrawerOpen(true)}
            aria-expanded={drawerOpen}
            aria-controls="ilab-drawer"
          >
            <span className="sr-only">{drawerOpen ? "Close menu" : "Open menu"}</span>
            <span className="ilab-header__burger-lines" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>
        </div>
      </header>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        active={active}
        onNavigate={go}
      />
    </>
  );
}
